/** Descuentos programados: un % por producto, categoría o marca con fechas de vigencia. Se aplican solos al vender. */
import { descuentoProgramadoInput, Money, type AlcanceDescuento, type DescuentoProgramadoDTO, type ProductoResumenDTO, type SucursalDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { BotonActivo } from '@/componentes/BotonActivo.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { PaginadorBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { normalizarDecimal } from '@/lib/montos.ts';
import { claves } from '@/lib/query.ts';
import { useActivarDescuento, useCategorias, useCrearDescuento, useDescuentos, useProductos } from './api.ts';

const hoyCaracas = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(new Date());
/** "2026-10-09" → "09/10/2026" sin pasar por Date (evita el corrimiento de zona horaria). */
const fechaCorta = (d: string) => d.slice(0, 10).split('-').reverse().join('/');

const ESTADO: Record<DescuentoProgramadoDTO['estado'], { texto: string; variante: 'ok' | 'info' | 'neutro' }> = {
  vigente: { texto: 'Vigente', variante: 'ok' },
  programado: { texto: 'Programado', variante: 'info' },
  vencido: { texto: 'Vencido', variante: 'neutro' },
  inactivo: { texto: 'Desactivado', variante: 'neutro' },
};
const ALCANCE: Record<AlcanceDescuento, string> = { producto: 'Un producto', categoria: 'Una categoría', marca: 'Una marca' };
/** Productos por página en el buscador del descuento. */
const LIMITE_PRODUCTOS = 30;

export function DialogDescuentos({ abierto, cerrar }: { abierto: boolean; cerrar: () => void }) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-4xl" data-testid="dialogo-descuentos">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Descuentos programados</DialogTitle><AyudaVentana tema="productos.descuentos" /></div>
          <DialogDescription>Un porcentaje por producto, categoría o marca entre dos fechas. Se aplica solo en el punto de venta y en las cotizaciones; no pide clave.</DialogDescription>
        </DialogHeader>
        {abierto ? <Contenido /> : null}
      </DialogContent>
    </Dialog>
  );
}

function Contenido() {
  const { puede, usuario } = useAuth();
  const puedeEditar = puede('productos.descuentos');
  const lista = useDescuentos();
  const crear = useCrearDescuento();
  const activar = useActivarDescuento();
  const categorias = useCategorias();
  const sucursales = useQuery({ queryKey: claves.sucursales, queryFn: () => get<SucursalDTO[]>('/sucursales'), enabled: puedeEditar && usuario?.rol === 'superadmin', staleTime: 5 * 60_000 });
  const [nombre, setNombre] = useState('');
  const [alcance, setAlcance] = useState<AlcanceDescuento>('producto');
  const [qProducto, setQProducto] = useState('');
  const [productoId, setProductoId] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [marca, setMarca] = useState('');
  const [porcentaje, setPorcentaje] = useState('');
  const [desde, setDesde] = useState(hoyCaracas);
  const [hasta, setHasta] = useState(hoyCaracas);
  const [sucursalId, setSucursalId] = useState('');
  // De 30 en 30 con paginador debajo de la lista (pedido de la dueña 2026-10-20)
  const [paginaProducto, setPaginaProducto] = usePaginaBusqueda(qProducto);
  const productos = useProductos({ q: qProducto || undefined, activo: 'true', pagina: paginaProducto, limite: LIMITE_PRODUCTOS });
  // El producto elegido sigue en la lista aunque se pase de página o se busque otra cosa
  const [elegido, setElegido] = useState<ProductoResumenDTO | null>(null);
  const deLaPagina = productos.data?.datos ?? [];
  const opcionesProducto = elegido && elegido.id === productoId && !deLaPagina.some((p) => p.id === elegido.id) ? [elegido, ...deLaPagina] : deLaPagina;

  const guardar = () => {
    const r = descuentoProgramadoInput.safeParse({
      nombre,
      alcance,
      ...(alcance === 'producto' && productoId ? { producto_id: productoId } : {}),
      ...(alcance === 'categoria' && categoriaId ? { categoria_id: categoriaId } : {}),
      ...(alcance === 'marca' && marca.trim() ? { marca: marca.trim() } : {}),
      porcentaje: normalizarDecimal(porcentaje),
      desde,
      hasta,
      ...(sucursalId ? { sucursal_id: sucursalId } : {}),
    });
    if (!r.success) {
      toast.error(r.error.issues[0]?.message ?? 'Revisa los datos del descuento');
      return;
    }
    crear.mutate(r.data, {
      onSuccess: (d) => {
        toast.success(`Descuento "${d.nombre}" programado`, { description: `${Money.parse(d.porcentaje).toDisplay()} % a ${d.objetivo}, del ${fechaCorta(d.desde)} al ${fechaCorta(d.hasta)}.` });
        setNombre('');
        setPorcentaje('');
        setProductoId('');
        setCategoriaId('');
        setMarca('');
      },
      onError: (e) => toast.error('No se pudo programar el descuento', { description: mensajeDeError(e) }),
    });
  };

  return (
    <div className="space-y-4">
      {puedeEditar ? (
        <div className="space-y-3 rounded-xl border bg-muted/40 p-3" data-testid="form-descuento">
          <div className="grid grid-cols-4 gap-3">
            <Campo etiqueta="Nombre" htmlFor="desc-nombre" className="col-span-2">
              <Input id="desc-nombre" placeholder="Semana del pego" value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="desc-nombre" />
            </Campo>
            <Campo etiqueta="Se aplica a" htmlFor="desc-alcance">
              <Select id="desc-alcance" value={alcance} onChange={(e) => setAlcance(e.target.value as AlcanceDescuento)} data-testid="desc-alcance">
                {(Object.keys(ALCANCE) as AlcanceDescuento[]).map((a) => (
                  <option key={a} value={a}>{ALCANCE[a]}</option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Descuento %" htmlFor="desc-porcentaje">
              <Input id="desc-porcentaje" inputMode="decimal" placeholder="10" value={porcentaje} onChange={(e) => setPorcentaje(e.target.value)} className="text-right" data-testid="desc-porcentaje" />
            </Campo>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2">
              {alcance === 'producto' ? (
                <Campo etiqueta="Producto" htmlFor="desc-producto">
                  <div className="flex gap-2">
                    <Input placeholder="Buscar…" value={qProducto} onChange={(e) => setQProducto(e.target.value)} className="w-36" data-testid="desc-producto-buscar" />
                    <Select id="desc-producto" value={productoId} onChange={(e) => { setProductoId(e.target.value); setElegido(opcionesProducto.find((p) => p.id === e.target.value) ?? null); }} data-testid="desc-producto">
                      <option value="">Elige el producto…</option>
                      {opcionesProducto.map((p) => (
                        <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>
                      ))}
                    </Select>
                  </div>
                  <PaginadorBusqueda pagina={paginaProducto} limite={LIMITE_PRODUCTOS} total={productos.data?.total ?? 0} onPagina={setPaginaProducto} testid="desc-producto" className="mt-1 rounded-lg border" />
                </Campo>
              ) : alcance === 'categoria' ? (
                <Campo etiqueta="Categoría" htmlFor="desc-categoria">
                  <Select id="desc-categoria" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} data-testid="desc-categoria">
                    <option value="">Elige la categoría…</option>
                    {(categorias.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>{c.padre_id ? `— ${c.nombre}` : c.nombre}</option>
                    ))}
                  </Select>
                </Campo>
              ) : (
                <Campo etiqueta="Marca" htmlFor="desc-marca" ayuda="Tal como está escrita en la ficha de los productos">
                  <Input id="desc-marca" value={marca} onChange={(e) => setMarca(e.target.value)} data-testid="desc-marca" />
                </Campo>
              )}
            </div>
            <Campo etiqueta="Desde" htmlFor="desc-desde">
              <Input id="desc-desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="desc-desde" />
            </Campo>
            <Campo etiqueta="Hasta" htmlFor="desc-hasta">
              <Input id="desc-hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="desc-hasta" />
            </Campo>
          </div>
          <div className="flex items-end justify-between gap-3">
            <Campo etiqueta="Tienda" htmlFor="desc-sucursal">
              <Select id="desc-sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)} className="w-64" data-testid="desc-sucursal">
                <option value="">Todas las tiendas</option>
                {(sucursales.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
                {!sucursales.data && usuario?.sucursal_id ? <option value={usuario.sucursal_id}>Solo {usuario.sucursal_nombre}</option> : null}
              </Select>
            </Campo>
            <Button onClick={guardar} cargando={crear.isPending} data-testid="desc-guardar">Programar descuento</Button>
          </div>
        </div>
      ) : null}
      <div className="max-h-[45vh] overflow-y-auto">
        <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
          {(d) =>
            d.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="descuentos-vacio">No hay descuentos programados.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Descuento</TableHead>
                    <TableHead>Se aplica a</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead>Vigencia</TableHead>
                    <TableHead>Tienda</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.map((x) => (
                    <TableRow key={x.id} data-testid={`descuento-${x.nombre}`}>
                      <TableCell>
                        <p className="font-medium">{x.nombre}</p>
                        <p className="text-[11px] text-muted-foreground">{x.creado_por_nombre}</p>
                      </TableCell>
                      <TableCell>
                        <span className="text-[11px] text-muted-foreground">{ALCANCE[x.alcance].replace(/^Una? /, '')}: </span>
                        {x.objetivo}
                      </TableCell>
                      <TableCell className="text-right num font-semibold">{Money.parse(x.porcentaje).toDisplay()}</TableCell>
                      <TableCell className="num">{fechaCorta(x.desde)} – {fechaCorta(x.hasta)}</TableCell>
                      <TableCell>{x.sucursal_nombre ?? 'Todas'}</TableCell>
                      <TableCell><Badge variant={ESTADO[x.estado].variante}>{ESTADO[x.estado].texto}</Badge></TableCell>
                      <TableCell className="text-right">
                        {puedeEditar && x.estado !== 'vencido' ? (
                          <BotonActivo
                            activo={x.activo}
                            titulo={`Desactivar el descuento «${x.nombre}»`}
                            consecuencia="El descuento deja de aplicarse en el punto de venta y en las cotizaciones desde ya; las ventas que ya lo llevan quedan igual."
                            disabled={activar.isPending}
                            alCambiar={(activo) => activar.mutateAsync({ id: x.id, activo }).then(() => { toast.success(activo ? 'Descuento activado' : 'Descuento desactivado'); }, (e) => { toast.error(mensajeDeError(e)); })}
                            testid={`descuento-alternar-${x.nombre}`}
                          />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          }
        </EstadoQuery>
      </div>
    </div>
  );
}
